import { http, tokenStorage } from '../client'
import { USE_MOCKS } from '../config'
import { endpoints } from '../endpoints'
import { delay, mockDb } from '../mock/db'
import { ApiError } from '../types'
import type {
  AuthSession,
  ChangeEmailPayload,
  ChangePasswordPayload,
  LoginPayload,
  ProfileUpdatePayload,
  RegisterPayload,
  User,
} from '../types'

/**
 * No modo mock, qualquer senha com 6+ caracteres entra. Um e-mail contendo
 * "admin" recebe papel de administrador, para dar para navegar o /admin sem
 * backend.
 */
function mockUserFrom(email: string, displayName?: string): User {
  return {
    id: 'u-demo',
    email,
    displayName: displayName ?? email.split('@')[0],
    username: null,
    firstName: null,
    lastName: null,
    nickname: null,
    cpf: null,
    address: null,
    occupation: null,
    phone: null,
    role: email.toLowerCase().includes('admin') ? 'admin' : 'user',
    emailVerified: true,
    createdAt: new Date().toISOString(),
  }
}

export async function login(payload: LoginPayload): Promise<AuthSession> {
  if (!USE_MOCKS) {
    const session = await http.post<AuthSession>(endpoints.auth.login, payload)
    tokenStorage.set(session.accessToken)
    return session
  }

  if (payload.password.length < 6) {
    throw new ApiError('E-mail ou senha inválidos.', 401, 'invalid_credentials')
  }
  const user = mockUserFrom(payload.email)
  mockDb.update({ user })
  tokenStorage.set('mock-token')
  return delay({ user, accessToken: 'mock-token' }, 500)
}

export async function register(payload: RegisterPayload): Promise<AuthSession> {
  if (!USE_MOCKS) {
    const session = await http.post<AuthSession>(endpoints.auth.register, payload)
    tokenStorage.set(session.accessToken)
    return session
  }

  if (payload.password.length < 6) {
    throw new ApiError('A senha precisa ter ao menos 6 caracteres.', 422, 'weak_password', {
      password: 'A senha precisa ter ao menos 6 caracteres.',
    })
  }
  const user = mockUserFrom(payload.email, payload.name)
  mockDb.update({ user })
  tokenStorage.set('mock-token')
  return delay({ user, accessToken: 'mock-token' }, 500)
}

export async function logout(): Promise<void> {
  if (!USE_MOCKS) {
    await http.post<void>(endpoints.auth.logout).catch(() => {
      /* sessão já expirada no servidor: seguir com o logout local */
    })
  } else {
    mockDb.update({ user: null })
  }
  tokenStorage.set(null)
}

/** Retorna `null` quando não há sessão — não lança em 401. */
export async function getSession(): Promise<User | null> {
  if (!USE_MOCKS) {
    if (!tokenStorage.get()) return null
    try {
      return await http.get<User>(endpoints.auth.me)
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        tokenStorage.set(null)
        return null
      }
      throw error
    }
  }
  return delay(mockDb.state.user, 80)
}

export function requestPasswordReset(email: string): Promise<void> {
  if (!USE_MOCKS) {
    return http.post<void>(endpoints.auth.forgotPassword, { email })
  }
  return delay(undefined, 600)
}

export async function updateProfile(payload: ProfileUpdatePayload): Promise<User> {
  if (!USE_MOCKS) return http.patch<User>(endpoints.profile.update, payload)

  const current = mockDb.state.user
  if (!current) throw new ApiError('Sessão expirada.', 401, 'unauthorized')

  const updated: User = { ...current, ...payload }
  mockDb.update({ user: updated })
  return delay(updated, 400)
}

export function changeEmail(payload: ChangeEmailPayload): Promise<void> {
  if (!USE_MOCKS) return http.post<void>(endpoints.auth.changeEmail, payload)

  if (payload.newEmail === mockDb.state.user?.email) {
    return Promise.reject(
      new ApiError('Novo e-mail não pode ser igual ao atual', 422, 'same_email', {
        newEmail: 'Novo e-mail não pode ser igual ao atual',
      }),
    )
  }
  return delay(undefined, 500)
}

export function changePassword(payload: ChangePasswordPayload): Promise<void> {
  if (!USE_MOCKS) return http.post<void>(endpoints.auth.changePassword, payload)

  if (payload.newPassword.length < 6) {
    return Promise.reject(
      new ApiError('A senha precisa ter ao menos 6 caracteres.', 422, 'weak_password', {
        newPassword: 'A senha precisa ter ao menos 6 caracteres.',
      }),
    )
  }
  return delay(undefined, 500)
}
